import React from "react";
import * as Sentry from "@sentry/react";
import {
  BrowserRouter,
  Route,
  Routes as RRDRoutes,
  useNavigate,
  useParams,
} from "react-router-dom";

import { RestrictedArea } from "@scenes/restrictedArea";

import { useAuthentication, useLogOut } from "@planzone/view-models";
import { Modal } from "@root/client/modal";
import { AllocationWarningsGuard } from "@components/allocationWarningsGuard";
import { UtilityPage } from "@pages/utilityPage";
import { Layout } from "@common/layout";
import { PersonalSpacePage } from "@pages/personalSpacePage";
import { ZonesPage } from "@pages/zonesPage";
import { ZonePage } from "@pages/zonePage";
import { PlanzoneMembersPage } from "@pages/planzoneMembersPage";
import { ObsPage } from "@pages/obsPage";
import { CapacityPage } from "@pages/capacityPage";
import { WorkloadPage } from "@pages/workloadPage";
import { OccupancyPage } from "@pages/occupancyPage";
import { ProjectPage } from "@pages/projectPage";
import { LandingPage } from "@pages/landingPage";
import { AccountCreationConfirmationPage } from "@pages/accountCreationConfirmationPage";
import { AccountPage } from "@pages/accountPage";
import { CalendarPage } from "@pages/calendarPage";
import { PlanzoneParametersPage } from "@pages/planzoneParametersPage";
import { ProjectPortfoliosPage } from "@pages/projectPortfoliosPage";
import { FillAccountPage } from "@pages/fillAccountPage";
import { SearchPage } from "@pages/searchPage";
import { CostProfilesPage } from "@pages/costProfilesPage";
import { TaskPage } from "@pages/taskPage";
import { ListPage } from "@pages/listPage";
import { EventPage } from "@pages/eventPage";
import { MilestonePage } from "@pages/milestonePage";
import { ExportsPage } from "@pages/exportsPage";
import { DocumentsPage } from "@pages/documentsPage";
import { DummyPage } from "@pages/dummyPage";
import { PlanzoneLogsPage } from "@pages/planzoneLogsPage";
import { AssistantPage } from "@pages/assistantPage";
import { NotificationsPage } from "@pages/notificationsPage";
import { NotFoundPage } from "@pages/notFoundPage";
import { Props } from "./types";
import { RouteLoader } from "./routeLoader";
import { Redirect } from "./redirect";
import { Loader } from "./loader";

function ParamsProvider({
  children,
}: {
  children: (params: { [key: string]: string }) => React.ReactNode;
}) {
  const params = useParams();

  return (
    <React.Fragment>
      {children(
        // @ts-ignore
        typeof params === "string" || typeof params === "undefined"
          ? {}
          : params,
      )}
    </React.Fragment>
  );
}

function HistoryProvider({
  children,
}: {
  children: (params: { push: (path: string) => void }) => React.ReactNode;
}) {
  const navigate = useNavigate();

  return <React.Fragment>{children({ push: navigate })}</React.Fragment>;
}

const Routes = Sentry.withSentryReactRouterV6Routing(RRDRoutes);

function Router({ isAppLoaded, queries }: Props) {
  const { hasSession } = useAuthentication();
  const { isRequestPending } = useLogOut();

  return (
    <BrowserRouter>
      <Redirect />
      <Modal>
        <AllocationWarningsGuard />
        {isAppLoaded && !isRequestPending ? (
          <React.Suspense fallback={<RouteLoader />}>
            {hasSession ? (
              <RestrictedArea
                areas={[
                  "personalSynthesis",
                  "personalTimesheet",
                  "resources",
                  "costProfiles",
                  "projectTemplates",
                  "projectReportsTemplate",
                  "projectStatuses",
                  "secondaryCriteriaTemplate",
                  "portfolios",
                  "calendar",
                  "strategicAxes",
                  "exports",
                  "documents",
                  "logs",
                  "capacity",
                  "workload",
                  "occupancy",
                ]}
              >
                {({
                  canAccessPersonalTimesheet,
                  canAccessPersonalSynthesis,
                  canAccessProjectTemplates,
                  canAccessProjectReportsTemplate,
                  canAccessProjectStatuses,
                  canAccessSecondaryCriteriaTemplate,
                  canAccessResources,
                  canAccessCalendar,
                  canAccessCostProfiles,
                  canAccessPortfolios,
                  canAccessExports,
                  canAccessDocuments,
                  canAccessLogs,
                  canAccessCapacity,
                  canAccessWorkload,
                  canAccessOccupancy,
                  canAccessStrategicAxes,
                }) => (
                  <Routes>
                    <Route
                      path="/registration/confirmation"
                      element={<AccountCreationConfirmationPage />}
                    />
                    <Route
                      path="/account/validation"
                      element={
                        <FillAccountPage token={queries.get("token") || ""} />
                      }
                    />
                    <Route
                      path="/"
                      element={
                        canAccessPersonalTimesheet ||
                        canAccessPersonalSynthesis ? (
                          <Layout>
                            <PersonalSpacePage slug="synthesis" />
                          </Layout>
                        ) : (
                          <HistoryProvider>
                            {({ push }) => (
                              <ZonesPage
                                zoneId={""}
                                slug="projects"
                                getZone={({ uuid }) => push(`/zones/${uuid}`)}
                                getAllZone={() => push("/zones/")}
                              />
                            )}
                          </HistoryProvider>
                        )
                      }
                    />
                    <Route
                      path="/time-sheets"
                      element={
                        <Layout>
                          <PersonalSpacePage slug="timeSheet" />
                        </Layout>
                      }
                    />
                    <Route path="/zones/*">
                      <Route
                        path="projects"
                        element={
                          <HistoryProvider>
                            {({ push }) => (
                              <ZonesPage
                                zoneId={""}
                                slug="projects"
                                getZone={({ uuid }) => push(`/zones/${uuid}`)}
                                getAllZone={() => push("/zones/")}
                              />
                            )}
                          </HistoryProvider>
                        }
                      />
                      <Route
                        path="lists"
                        element={
                          <HistoryProvider>
                            {({ push }) => (
                              <ZonesPage
                                zoneId={""}
                                slug="lists"
                                getZone={({ uuid }) => push(`/zones/${uuid}`)}
                                getAllZone={() => push("/zones/")}
                              />
                            )}
                          </HistoryProvider>
                        }
                      />
                      <Route
                        path="gantt"
                        element={
                          <HistoryProvider>
                            {({ push }) => (
                              <ZonesPage
                                zoneId={""}
                                slug="gantt"
                                getZone={({ uuid }) => push(`/zones/${uuid}`)}
                                getAllZone={() => push("/zones/")}
                              />
                            )}
                          </HistoryProvider>
                        }
                      />
                      <Route
                        path="reports"
                        element={
                          <HistoryProvider>
                            {({ push }) => (
                              <ZonesPage
                                zoneId={""}
                                slug="reports"
                                getZone={({ uuid }) => push(`/zones/${uuid}`)}
                                getAllZone={() => push("/zones/")}
                              />
                            )}
                          </HistoryProvider>
                        }
                      />
                      <Route
                        path="workloads"
                        element={
                          <HistoryProvider>
                            {({ push }) => (
                              <ZonesPage
                                zoneId={""}
                                slug="workloads"
                                getZone={({ uuid }) => push(`/zones/${uuid}`)}
                                getAllZone={() => push("/zones/")}
                              />
                            )}
                          </HistoryProvider>
                        }
                      />
                      <Route
                        path=":uuid/*"
                        element={
                          <ParamsProvider>
                            {({ uuid }) => (
                              <Layout>
                                <ZonePage zoneId={uuid} />
                              </Layout>
                            )}
                          </ParamsProvider>
                        }
                      />
                      <Route
                        path="*"
                        element={
                          <HistoryProvider>
                            {({ push }) => (
                              <ZonesPage
                                zoneId={""}
                                slug="zones"
                                getZone={({ uuid }) => push(`/zones/${uuid}`)}
                                getAllZone={() => push("/zones/")}
                              />
                            )}
                          </HistoryProvider>
                        }
                      />
                    </Route>
                    <Route
                      path="/projects/:projectId/*"
                      element={
                        <ParamsProvider>
                          {({ projectId }) => (
                            <Layout>
                              <ProjectPage projectId={projectId} />
                            </Layout>
                          )}
                        </ParamsProvider>
                      }
                    />
                    {/*<Route*/}
                    {/*  path="/zones/:uuid/projects"*/}
                    {/*  element={*/}
                    {/*    <ParamsProvider>*/}
                    {/*      {({ uuid }) => (*/}
                    {/*        <Layout>*/}
                    {/*          <ZonePage zoneId={uuid} />*/}
                    {/*        </Layout>*/}
                    {/*      )}*/}
                    {/*    </ParamsProvider>*/}
                    {/*  }*/}
                    {/*/>*/}
                    {canAccessResources && (
                      <Route
                        path="/members"
                        element={
                          <Layout>
                            <PlanzoneMembersPage />
                          </Layout>
                        }
                      />
                    )}
                    {canAccessResources && (
                      <Route
                        path="/organizational-units"
                        element={
                          <Layout>
                            <ObsPage />
                          </Layout>
                        }
                      />
                    )}
                    {canAccessCostProfiles && (
                      <Route
                        path="/cost-profiles"
                        element={
                          <Layout>
                            <CostProfilesPage />
                          </Layout>
                        }
                      />
                    )}
                    {canAccessCapacity && (
                      <Route
                        path="/capacity"
                        element={
                          <Layout>
                            <CapacityPage />
                          </Layout>
                        }
                      />
                    )}
                    {canAccessWorkload && (
                      <Route
                        path="/workload"
                        element={
                          <Layout>
                            <WorkloadPage />
                          </Layout>
                        }
                      />
                    )}
                    {canAccessOccupancy && (
                      <Route
                        path="/occupancy"
                        element={
                          <Layout>
                            <OccupancyPage />
                          </Layout>
                        }
                      />
                    )}
                    <Route
                      path="/search"
                      element={
                        <Layout>
                          <SearchPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/account"
                      element={
                        <Layout>
                          <AccountPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/tasks/:uuid"
                      element={
                        <ParamsProvider>
                          {({ uuid }) => (
                            <Layout>
                              <TaskPage taskId={uuid} />
                            </Layout>
                          )}
                        </ParamsProvider>
                      }
                    />
                    <Route
                      path="/lists/:uuid"
                      element={
                        <ParamsProvider>
                          {({ uuid }) => (
                            <Layout>
                              <ListPage listId={uuid} />
                            </Layout>
                          )}
                        </ParamsProvider>
                      }
                    />
                    <Route
                      path="/events/:uuid"
                      element={
                        <ParamsProvider>
                          {({ uuid }) => (
                            <Layout>
                              <EventPage eventId={uuid} />
                            </Layout>
                          )}
                        </ParamsProvider>
                      }
                    />
                    <Route
                      path="/milestones/:uuid"
                      element={
                        <ParamsProvider>
                          {({ uuid }) => (
                            <Layout>
                              <MilestonePage milestoneId={uuid} />
                            </Layout>
                          )}
                        </ParamsProvider>
                      }
                    />
                    {(canAccessProjectTemplates ||
                      canAccessProjectReportsTemplate ||
                      canAccessProjectStatuses ||
                      canAccessSecondaryCriteriaTemplate ||
                      canAccessStrategicAxes) && (
                      <Route
                        path="/utilities/*"
                        element={
                          <Layout>
                            <UtilityPage />
                          </Layout>
                        }
                      />
                    )}
                    {canAccessDocuments && (
                      <Route
                        path="/documents/*"
                        element={
                          <Layout>
                            <DocumentsPage />
                          </Layout>
                        }
                      />
                    )}
                    {canAccessPortfolios && (
                      <Route
                        path="/portfolios"
                        element={
                          <Layout>
                            <ProjectPortfoliosPage />
                          </Layout>
                        }
                      />
                    )}
                    {canAccessCalendar && (
                      <Route
                        path="/calendar"
                        element={
                          <Layout>
                            <CalendarPage />
                          </Layout>
                        }
                      />
                    )}
                    {canAccessExports && (
                      <Route
                        path="/exports"
                        element={
                          <Layout>
                            <ExportsPage />
                          </Layout>
                        }
                      />
                    )}
                    <Route
                      path="/planzone/parameters"
                      element={
                        <Layout>
                          <PlanzoneParametersPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/notifications"
                      element={
                        <Layout>
                          <NotificationsPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/assistant"
                      element={
                        <Layout>
                          <AssistantPage />
                        </Layout>
                      }
                    />
                    <Route path="/duck" element={<DummyPage />} />
                    {canAccessLogs && (
                      <Route
                        path="/logs"
                        element={
                          <Layout>
                            <PlanzoneLogsPage />
                          </Layout>
                        }
                      />
                    )}
                    <Route
                      path="*"
                      element={
                        <Layout>
                          <NotFoundPage />
                        </Layout>
                      }
                    />
                  </Routes>
                )}
              </RestrictedArea>
            ) : (
              <Routes>
                <Route
                  path="/registration/confirmation"
                  element={<AccountCreationConfirmationPage />}
                />
                <Route
                  path="/account/validation"
                  element={
                    <FillAccountPage token={queries.get("token") || ""} />
                  }
                />
                <Route path="/duck" element={<DummyPage />} />
                <Route path="*" element={<LandingPage />} />
              </Routes>
            )}
          </React.Suspense>
        ) : (
          <Loader />
        )}
      </Modal>
    </BrowserRouter>
  );
}

export { Router };
